import React, { useEffect } from "react";

import { incomplete, useGetAllPopupsForATypeAdmin, useNavigate, useParams, useUpdateAdminPopup } from "utils";

import { LANGUAGES } from "consts";

import { Popup } from "models";

import { AdminHead, Button, Col, ErrorBoundary, Form, Input, MainPage, Option, Row, Select, Tabs } from "components";

import { PopupInputs } from "pages/admin/content/components/PopupInputs";

type PopupContent = {
  button: boolean;
  buttonLink: string;
  video: string;
  [key: `${string}-${"headline" | "content" | "buttonText"}`]: string; // e.g., 'de-headline', 'pl-content'
};

export const PopupUpdate = () => {
  const { id } = useParams();
  const { data: popups, isPending: isLoadingPopups } = useGetAllPopupsForATypeAdmin(id || "");
  const { mutate: updatePopups, isPending: isSavingPopups, isSuccess: updatedSuccessfully } = useUpdateAdminPopup();
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const complete = (formData: PopupContent) => {
    if (!popups) return;
    const popupRequest: Popup[] = popups.map((popup) => ({
      ...popup,
      video: formData.video,
      buttonLink: formData.buttonLink,
      button: formData.button,
      buttonText: formData[`${popup.language}-buttonText`] ? formData[`${popup.language}-buttonText`] : popup.buttonText,
      headline: formData[`${popup.language}-headline`] ? formData[`${popup.language}-headline`] : popup.headline,
      content: formData[`${popup.language}-content`] ? formData[`${popup.language}-content`] : popup.content,
    }));

    updatePopups(popupRequest);
  };

  const getTabPanes = () => {
    if (!popups) return;
    const tabPanes: { label: string; key: string; children: React.ReactNode }[] = [];
    LANGUAGES.filter((language) => language.showOnAdmin).forEach((language) => {
      const { code: languageCode } = language;
      const popup = popups?.find((popup) => popup.language === languageCode);
      if (!popup) {
        return;
      }
      tabPanes.push({
        label: language.language,
        key: languageCode,
        children: (
          <ErrorBoundary>
            <PopupInputs popup={popup} />
          </ErrorBoundary>
        ),
      });
    });

    return tabPanes;
  };

  useEffect(() => {
    if (updatedSuccessfully) {
      navigate("/content/popups");
    }
  }, [updatedSuccessfully, navigate]);

  return (
    <MainPage loading={isLoadingPopups || isSavingPopups} pageName={"admin"}>
      <ErrorBoundary>
        <AdminHead
          description={
            "Update the popup content here. Content for each type of popup is required for the app to function, so you cannot add or disable popups. The button can be an internal or external link - always copy the FULL URL."
          }
          link={"/content/popups"}
          headline={"Update a popup"}
          tooltipText={"Back to popups"}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <Row className="midi-form">
          <Col>
            <Form form={form} name="confirm" onFinish={(v) => complete(v)} onFinishFailed={incomplete}>
              <Row>
                {/* prelint-ignore-col */}
                {popups && popups.length ? (
                  <Col>
                    <p>
                      Popup type: <b>{popups[0].type}</b>
                    </p>
                  </Col>
                ) : null}
                <Col sm={24} md={12}>
                  {/* @ts-expect-error convert to TS */}
                  <Select
                    name="button"
                    label="Show button"
                    rules={[{ required: true }]}
                    initialValue={popups ? (popups[0].button ? popups[0].button : false) : false}
                  >
                    <Option value={true}>Show button</Option>
                    <Option value={false}>Don't show button</Option>
                  </Select>
                </Col>
                <Col sm={24} md={12}>
                  <Input
                    name="buttonLink"
                    label="Button link"
                    rules={[{ required: false }]}
                    initialValue={popups ? popups[0].buttonLink : ""}
                    disabled={popups ? popups[0].button : false}
                  />
                </Col>
                <Col sm={24} md={24} className="margin-bottom-200">
                  <Input name="video" label="Vimeo video ID" rules={[{ required: false }]} initialValue={popups ? popups[0].video : ""} />
                </Col>
              </Row>
              <Tabs items={getTabPanes()} />
              <Row className="margin-top-100">
                <Col>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" disabled={isLoadingPopups || isSavingPopups} loading={isLoadingPopups || isSavingPopups}>
                      Save
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </ErrorBoundary>
    </MainPage>
  );
};
